import mixpanel from 'mixpanel-browser';
import { useMixpanelTrackLink } from '../../analytics/mixpanel';
import { useContext, useState } from 'react';
import EVENTS from '../../analytics/events';
import { NbaModalsContext } from './index';
import { UpdateProjectDatesModalContext } from '../my-projects/Modals/UpdateProjectDatesModal';
import VimeoOnboardingVideo from '../home/VimeoOnboardingModal';
import {
  RespondActionsResponse,
  SurveyActionsResponse,
  StartDateActionsResponse,
  EndDateActionsResponse,
  DiscoveryOutcomeActionsResponse,
} from 'lib/client/src/platformapi.schemas';
import cx from 'classix';

// TODO: is this necessary if all come from api?
export const GenericNBACard = ({
  description,
  linkText,
  linkUrl,
  logo,
  leftBorder,
  avatarSize,
}: {
  description?: string;
  linkText?: string;
  linkUrl?: string;
  logo?: React.ReactNode;
  leftBorder?: boolean;
  avatarSize?: 'md' | 'lg';
}) => {
  return (
    <div
      className={cx(
        'flex flex-col bg-white p-4 gap-2 shadow rounded-lg relative [&:nth-last-child(2)]:mb-8 blue-links',
        leftBorder && 'border-l-4 border-l-accent-500',
      )}
    >
      <div className='flex gap-2 items-center '>
        {logo && (
          <div
            className={cx(
              avatarSize !== 'lg' && 'w-[50px] h-[50px]',
              avatarSize === 'lg' && 'w-[90px] h-[90px]',
            )}
          >
            {logo}
          </div>
        )}
        <div>{description}</div>
      </div>
      {linkUrl && (
        <div className='ml-auto'>
          <a href={linkUrl}>{linkText}</a>
        </div>
      )}
    </div>
  );
};

export type NextBestActionCardProps = {
  engagmentActionId: string;
  /** Note that NBAs can contain some amount of HTML, and that this is thus rendered
   * with dangerouslySetInnerHTML here
   *
   * This is very unfortunate! These contain links which we cannot reasonably
   * capture in Mixpanel outside of the automatic url tracking, which will fail
   * here if they're external to this new Platform app
   **/
  content: string;
  linkText: string;
  /** The "real" url that is strangely not actually set as the href target; see the docs for
   * get_next_best_actions in dashboard.py */
  linkUrl: string;
  actualUrl: string;
  estimatedTime: string;
  canDismiss: boolean;
  leftBorder?: boolean;
  actionData?:
    | RespondActionsResponse
    | SurveyActionsResponse
    | StartDateActionsResponse
    | EndDateActionsResponse
    | DiscoveryOutcomeActionsResponse;
};

export const NextBestActionCard = ({
  engagmentActionId: engagementActionId,
  content,
  linkText,
  linkUrl,
  actualUrl,
  estimatedTime,
  canDismiss,
  /** Display the left yellow highlight border. Used for true NBAs instead of "NBA-like" cards
   * TODO: Consider reworking, but I think a quick fix like this is OK given us reworking this page soon */
  leftBorder,
  actionData,
}: NextBestActionCardProps) => {
  const { setDismissNbaModelIsOpen, setDismissingEngagementId } = useContext(NbaModalsContext);
  const [showOnboardingVideoModal, setShowOnboardingVideoModal] = useState(false);
  const { openUpdateProjectDatesModal } = useContext(UpdateProjectDatesModalContext);
  const trackLinkRef = useMixpanelTrackLink<HTMLAnchorElement>(
    EVENTS.DASHBOARD.HOME.NBA_LINK_CLICKED,
    { engagementActionId },
  );

  const isOnboardingVideoLink = actualUrl.includes('player.vimeo');
  const isUpdateProjectDatesAction = engagementActionId.includes('project_end_date_action');

  return (
    <div
      className={cx(
        'bg-white p-4 shadow rounded-lg relative [&:last-child]:mb-8',
        leftBorder && 'border-l-4 border-l-accent-500',
      )}
    >
      <div
        className='pr-6 pb-4 text-gray-500 blue-links'
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />
      <div className='text-end blue-links'>
        {!isOnboardingVideoLink && !isUpdateProjectDatesAction && (
          <a ref={trackLinkRef} className='text-sm' href={linkUrl}>
            {linkText}
          </a>
        )}
        {isOnboardingVideoLink && (
          <>
            {/* Created as a button b/c a11y requires <a> tags have functional hrefs */}
            <button className='text-sm blue-link' onClick={() => setShowOnboardingVideoModal(true)}>
              {linkText}
            </button>
            <VimeoOnboardingVideo
              url={actualUrl}
              isOpen={showOnboardingVideoModal}
              setIsOpen={setShowOnboardingVideoModal}
              engagementActionId={engagementActionId}
            />
          </>
        )}
        {isUpdateProjectDatesAction && actionData && (
          <button
            className='text-sm blue-link'
            onClick={() => {
              openUpdateProjectDatesModal({
                // we don't show this nba for team assignments currently:
                id: (actionData as EndDateActionsResponse).projectId,
                startDate: new Date((actionData as EndDateActionsResponse).startDate),
                endDate: new Date((actionData as EndDateActionsResponse).endDate),
              });
            }}
          >
            {linkText}
          </button>
        )}
        {/* Note that the legacy app shows a ± icon here instead of a ~, which inadvertently looks like underlined text. */}
        <div className='text-[11px] text-gray-500'>~{estimatedTime}</div>
      </div>

      {canDismiss && (
        <button
          onClick={() => {
            setDismissingEngagementId(engagementActionId);
            setDismissNbaModelIsOpen(true);
            mixpanel.track(EVENTS.DASHBOARD.HOME.NBA_DISMISSED, {
              engagementActionId,
            });
          }}
          className='absolute right-0 top-0 w-10 h-10 flex items-center justify-center'
        >
          <span className='absolute bottom-[25px] close-icon' />
        </button>
      )}
    </div>
  );
};
